import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Send, Loader } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDateTime } from '../../utils/formatDate';

export default function LeadWhatsAppPanel({ leadId, phoneNumber }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/crm/leads/${leadId}/whatsapp-messages`);
      setMessages(data.messages || []);
    } catch (err) {
      alertService.error('Error', 'Failed to load WhatsApp history');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleSend = async () => {
    if (!text.trim() || !phoneNumber) return;
    setSending(true);
    try {
      await api.post('/meta/send', { recipient: phoneNumber, type: 'text', text: text.trim() });
      setText('');
      await fetchMessages();
    } catch (err) {
      alertService.error('Send Failed', err.response?.data?.error || 'Could not send WhatsApp message');
    } finally {
      setSending(false);
    }
  };

  if (!phoneNumber) {
    return (
      <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
        <MessageSquare size={48} className="mx-auto mb-4 opacity-10" />
        <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No Phone Number On File</p>
      </div>
    );
  }

  return (
    <div className="sf-card bg-white flex flex-col h-[560px] animate-in fade-in">
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader size={24} className="text-[#0176D3] animate-spin" />
          </div>
        ) : messages.length > 0 ? (
          messages.map((m) => (
            <div key={m._id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[70%] rounded px-4 py-2.5 text-[13px] font-medium ${
                  m.direction === 'outbound'
                    ? 'bg-[#0176D3] text-white'
                    : 'bg-[#F3F2F2] text-[#080707] border border-[#D8DDE6]'
                }`}
              >
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] mt-1 ${m.direction === 'outbound' ? 'text-white/70' : 'text-[#706E6B]'}`}>
                  {formatDateTime(m.createdAt)}
                  {m.direction === 'outbound' ? ` · ${m.status}` : ''}
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center opacity-40">
            <MessageSquare size={40} className="mb-3 opacity-20" />
            <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No WhatsApp Messages Yet</p>
          </div>
        )}
      </div>

      <div className="border-t border-[#D8DDE6] p-4 flex items-end gap-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={`Message ${phoneNumber}...`}
          rows={2}
          className="flex-1 px-3 py-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all resize-none focus:ring-1 focus:ring-[#0176D3]"
        />
        <button
          onClick={handleSend}
          disabled={sending || !text.trim()}
          className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#0176D3] text-white rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] hover:bg-[#014486] disabled:opacity-40 transition-all"
        >
          {sending ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
